// frontend/src/pages/configuracoes/auditoria/HistoricoRegistroModal.js

import React, { useEffect, useState } from "react";
import api from "../../../services/api";
import { formatDateWithHour } from "../../../utils/date";

const operacoesLabel = {
    create: "Criação",
    update: "Atualização",
    delete: "Exclusão",
    upsert: "Upsert",
    deleteMany: "Exclusão em massa",
};

const operacaoBadgeClass = (operacao) => {
    switch (operacao) {
        case "create":
            return "bg-emerald-100 text-emerald-800 border-emerald-200";
        case "update":
            return "bg-amber-100 text-amber-800 border-amber-200";
        case "delete":
        case "deleteMany":
            return "bg-red-100 text-red-800 border-red-200";
        case "upsert":
            return "bg-indigo-100 text-indigo-800 border-indigo-200";
        default:
            return "bg-stone-100 text-stone-800 border-stone-200";
    }
};

const operacoesNavLabel = {
    create: "criado(s)",
    update: "atualizado(s)",
    upsert: "criado/atualizado(s)",
    delete: "removido(s)",
    deleteMany: "exclusão em massa",
    updateMany: "atualização em massa",
};

const RelOpResumo = ({ relOp }) => {
    const partes = (relOp.operacoes || []).map((op, idx) => {
        if (op.tipo === "connect" || op.tipo === "set") {
            const itens = (op.ids || []).map((id, i) => ({
                id,
                label: op.labels?.[i] ?? null,
            }));
            const verbo = op.tipo === "connect" ? "vinculado(s)" : "definido(s) como";
            return (
                <div key={idx} className="text-sm text-stone-700">
                    <span className="font-medium">{relOp.target}</span> {verbo}:
                    <div className="flex flex-wrap gap-1.5 mt-1">
                        {itens.map((it) => (
                            <span
                                key={it.id}
                                className="inline-flex items-baseline gap-1 bg-stone-100 border border-stone-200 rounded px-2 py-0.5"
                            >
                                <span className="text-stone-700">{it.label || `#${it.id}`}</span>
                                {it.label && <span className="text-xs text-stone-400">#{it.id}</span>}
                            </span>
                        ))}
                    </div>
                </div>
            );
        }
        if (op.tipo === "disconnect") {
            return (
                <div key={idx} className="text-sm text-stone-700">
                    <span className="font-medium">{relOp.target}</span> desvinculado
                    {Array.isArray(op.ids) && op.ids.length ? `: ${op.ids.length} item(ns)` : ""}.
                </div>
            );
        }
        if (op.count != null) {
            return (
                <div key={idx} className="text-sm text-stone-700">
                    <span className="font-medium">{op.count}</span> {relOp.target}{" "}
                    {operacoesNavLabel[op.tipo] || op.tipo}.
                </div>
            );
        }
        return (
            <div key={idx} className="text-sm text-stone-700">
                <span className="font-medium">{relOp.target}</span>:{" "}
                {operacoesNavLabel[op.tipo] || op.tipo}.
            </div>
        );
    });
    return <div className="flex flex-col gap-1">{partes}</div>;
};

const formatValor = (valor) => {
    if (valor === null || valor === undefined) return <span className="italic text-stone-400">vazio</span>;
    if (typeof valor === "boolean") return valor ? "Sim" : "Não";
    if (isPlainObject(valor) && valor.__relOp) {
        return <RelOpResumo relOp={valor} />;
    }
    if (typeof valor === "object") {
        return (
            <pre className="text-xs whitespace-pre-wrap break-all bg-stone-50 p-2 rounded border border-stone-200 max-h-40 overflow-auto">
                {JSON.stringify(valor, null, 2)}
            </pre>
        );
    }
    const str = String(valor);
    if (str.length > 200) {
        return (
            <pre className="text-xs whitespace-pre-wrap break-all bg-stone-50 p-2 rounded border border-stone-200 max-h-40 overflow-auto">
                {str}
            </pre>
        );
    }
    return str;
};

function isPlainObject(v) {
    return v !== null && typeof v === "object" && !Array.isArray(v) && !(v instanceof Date);
}

const RelacaoTag = ({ id, label }) => (
    <span className="inline-flex items-baseline gap-1.5">
        <span className="font-medium text-stone-700">{label}</span>
        <span className="text-xs text-stone-400">#{id}</span>
    </span>
);

const renderValorDiff = (valor, label) => {
    if (label != null && valor != null) {
        return <RelacaoTag id={valor} label={label} />;
    }
    return formatValor(valor);
};

const renderValorComRelacao = (campo, valor, relacoes) => {
    const rel = relacoes?.[campo];
    if (rel) {
        return <RelacaoTag id={rel.id} label={rel.label} />;
    }
    return formatValor(valor);
};

const HistoricoRegistroModal = ({ tabela, registroId, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");
    const [historico, setHistorico] = useState({ tabela, registroId, total: 0, eventos: [] });

    useEffect(() => {
        const carregar = async () => {
            setLoading(true);
            setErrorMsg("");
            try {
                const response = await api.get(
                    `/auditoria/historico/${encodeURIComponent(tabela)}/${registroId}`
                );
                setHistorico(response.data);
            } catch (err) {
                console.error("Erro ao carregar histórico do registro:", err);
                setErrorMsg(err.response?.data?.msg || "Erro ao carregar histórico do registro.");
            } finally {
                setLoading(false);
            }
        };
        carregar();
    }, [tabela, registroId]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                    <div>
                        <h2 className="text-lg font-semibold text-stone-800">Histórico do Registro</h2>
                        <p className="text-sm text-stone-500">
                            {tabela} · ID #{registroId} · {historico.total} evento(s)
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-stone-500 hover:text-stone-800 text-2xl leading-none"
                        aria-label="Fechar"
                    >
                        ×
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {loading ? (
                        <div className="text-center text-stone-600 py-10">Carregando histórico...</div>
                    ) : errorMsg ? (
                        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-3 text-sm">
                            {errorMsg}
                        </div>
                    ) : historico.eventos.length === 0 ? (
                        <div className="text-center text-stone-500 py-10">
                            Nenhum evento de auditoria encontrado para este registro.
                        </div>
                    ) : (
                        <div className="relative">
                            <div className="absolute left-4 top-0 bottom-0 w-px bg-stone-200" aria-hidden />
                            <ol className="flex flex-col gap-6">
                                {historico.eventos.map((evento, idx) => {
                                    const diffEntries = Object.entries(evento.diff || {});
                                    return (
                                        <li key={evento.id} className="relative pl-12">
                                            <div className="absolute left-1.5 top-1.5 w-5 h-5 rounded-full bg-white border-2 border-stone-400 flex items-center justify-center">
                                                <span className="text-[10px] font-semibold text-stone-600">
                                                    {idx + 1}
                                                </span>
                                            </div>

                                            <div className="border border-slate-200 rounded-md bg-white">
                                                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200">
                                                    <div className="flex items-center gap-3">
                                                        <span
                                                            className={`inline-block px-2 py-0.5 border rounded text-xs font-medium ${operacaoBadgeClass(
                                                                evento.operacao
                                                            )}`}
                                                        >
                                                            {operacoesLabel[evento.operacao] || evento.operacao}
                                                        </span>
                                                        <span className="text-sm text-stone-700 font-medium">
                                                            {formatDateWithHour(evento.data)}
                                                        </span>
                                                    </div>
                                                    <div className="text-sm text-stone-600">
                                                        {evento.usuario ? (
                                                            <span>
                                                                {evento.usuario.nome}{" "}
                                                                <span className="text-xs text-stone-400">
                                                                    ({evento.usuario.email})
                                                                </span>
                                                            </span>
                                                        ) : (
                                                            <span className="italic text-stone-400">Sistema</span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="px-4 py-3">
                                                    {evento.operacao === "create" ? (
                                                        <DadosCriacao
                                                            dados={evento.dadosNovos}
                                                            relacoes={evento.relacoes?.novos}
                                                        />
                                                    ) : evento.operacao === "delete" ||
                                                      evento.operacao === "deleteMany" ? (
                                                        <DadosExcluidos
                                                            dados={evento.dadosAntigos}
                                                            relacoes={evento.relacoes?.antigos}
                                                        />
                                                    ) : diffEntries.length === 0 ? (
                                                        <div className="text-sm text-stone-500 italic">
                                                            Nenhuma alteração detectada nos campos rastreados.
                                                        </div>
                                                    ) : (
                                                        <DiffTable diffEntries={diffEntries} />
                                                    )}
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ol>
                        </div>
                    )}
                </div>

                <div className="px-6 py-3 border-t border-slate-200 flex justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    );
};

const DiffTable = ({ diffEntries }) => (
    <div className="overflow-x-auto">
        <table className="min-w-full text-sm border-collapse">
            <thead>
                <tr className="bg-stone-50">
                    <th className="px-3 py-2 text-left border border-slate-200 w-1/4">Campo</th>
                    <th className="px-3 py-2 text-left border border-slate-200 w-1/3">Antes</th>
                    <th className="px-3 py-2 text-left border border-slate-200 w-1/3">Depois</th>
                </tr>
            </thead>
            <tbody>
                {diffEntries.map(([campo, valores]) => (
                    <tr key={campo} className="align-top">
                        <td className="px-3 py-2 border border-slate-200 font-medium text-stone-700">
                            {campo}
                        </td>
                        <td className="px-3 py-2 border border-slate-200 bg-red-50/40 break-words">
                            {renderValorDiff(valores.antes, valores.antesLabel)}
                        </td>
                        <td className="px-3 py-2 border border-slate-200 bg-emerald-50/40 break-words">
                            {renderValorDiff(valores.depois, valores.depoisLabel)}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const DadosCriacao = ({ dados, relacoes }) => {
    if (!dados || typeof dados !== "object") {
        return <div className="text-sm text-stone-500 italic">Sem dados disponíveis.</div>;
    }
    const entries = Object.entries(dados);
    if (entries.length === 0) {
        return <div className="text-sm text-stone-500 italic">Sem dados.</div>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-sm border-collapse">
                <thead>
                    <tr className="bg-stone-50">
                        <th className="px-3 py-2 text-left border border-slate-200 w-1/3">Campo</th>
                        <th className="px-3 py-2 text-left border border-slate-200">Valor</th>
                    </tr>
                </thead>
                <tbody>
                    {entries.map(([campo, valor]) => (
                        <tr key={campo} className="align-top">
                            <td className="px-3 py-2 border border-slate-200 font-medium text-stone-700">
                                {campo}
                            </td>
                            <td className="px-3 py-2 border border-slate-200 break-words">
                                {renderValorComRelacao(campo, valor, relacoes)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const DadosExcluidos = ({ dados, relacoes }) => {
    if (!dados || typeof dados !== "object") {
        return <div className="text-sm text-stone-500 italic">Sem dados disponíveis.</div>;
    }
    const entries = Object.entries(dados);
    if (entries.length === 0) {
        return <div className="text-sm text-stone-500 italic">Sem dados.</div>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-sm border-collapse">
                <thead>
                    <tr className="bg-stone-50">
                        <th className="px-3 py-2 text-left border border-slate-200 w-1/3">Campo</th>
                        <th className="px-3 py-2 text-left border border-slate-200">Valor antes da exclusão</th>
                    </tr>
                </thead>
                <tbody>
                    {entries.map(([campo, valor]) => (
                        <tr key={campo} className="align-top">
                            <td className="px-3 py-2 border border-slate-200 font-medium text-stone-700">
                                {campo}
                            </td>
                            <td className="px-3 py-2 border border-slate-200 bg-red-50/40 break-words">
                                {renderValorComRelacao(campo, valor, relacoes)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default HistoricoRegistroModal;
